import { useState, useEffect } from "react";
import { JobOffersAddWizard } from "@/components/jobs/JobOffersAddWizard";
import { JobCreationMethodModal } from "@/components/jobs/JobCreationMethodModal";
import { JobDescriptionPasteModal } from "@/components/jobs/JobDescriptionPasteModal";
import { JobDescriptionUploadModal } from "@/components/jobs/JobDescriptionUploadModal";
import { ProcessingOverlay } from "@/components/ui/ProcessingOverlay";
import { ModalForm } from "@/components/ModalForm";
import { useTranslation } from "react-i18next";

interface JobFormData {
  title: string;
  companyId: string;
  companyName: string;
  description: string;
  skills: string;
  contactId?: string;
  aiSummary: string;
  
  // Fields
  employmentType: string;
  timeCommitment: string;
  contractDuration: string;
  startDate: string;
  seniorityLevel: string;
  budget: string;
  hiringManager: string;
  locationType: string;
  workplaceBreakdown: string;
  hardSkills: string;
  softSkills: string;
  educationLevel: string;
  yearsOfExperience: string;
  languageRequirements: string;
  cultureTags: string;
  applicationDeadline: string;
  jobPostingDate: string;
  source: string;
  assignedRecruiter: string;
}

interface CompanyInfo {
  id: string;
  name: string;
}

interface JobOffersAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddJob: (jobData: any) => Promise<string | undefined>;
  initialCompany?: CompanyInfo;
}

type CreationStep = 'method' | 'upload' | 'paste' | 'form';

export const JobOffersAddModal = ({ isOpen, onClose, onAddJob, initialCompany }: JobOffersAddModalProps) => {
  const [currentStep, setCurrentStep] = useState<CreationStep>('method');
  const [formData, setFormData] = useState<Partial<JobFormData>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingStage, setProcessingStage] = useState('');
  const { t } = useTranslation();
  
  // Initialize company data from initialCompany when provided
  useEffect(() => {
    if (initialCompany && isOpen) {
      // Set the company info in the form data
      setFormData(prevData => ({
        ...prevData,
        companyId: initialCompany.id,
        companyName: initialCompany.name
      }));
    }
  }, [initialCompany, isOpen]);
  
  const handleAddJob = async (jobData: any) => {
    // Process the new job data to match the expected format from the parent
    console.log("JobOffersAddModal handleAddJob called with data:", jobData);
    
    try {
      // Call the parent handler with the processed data
      const result = await onAddJob(jobData);
      
      if (result) {
        console.log("Job created successfully with ID:", result);
        resetForm();
        return result;
      } else {
        console.error("Job creation failed - no ID returned");
        return undefined;
      }
    } catch (error) {
      console.error("Error in job creation:", error);
      throw error;
    }
  };

  const resetForm = () => {
    setFormData({});
    setCurrentStep('method');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleMethodSelect = (method: 'upload' | 'paste' | 'manual', file?: File) => {
    console.log("Method selected:", method);
    switch (method) {
      case 'upload':
        setCurrentStep('upload');
        break;
      case 'paste':
        setCurrentStep('paste');
        break;
      case 'manual':
        // If we have initialCompany, skip the method selection and go straight to form
        setCurrentStep('form');
        break;
    }
  };

  const handleProcessedData = (extractedData: any) => {
    console.log('Extracted Data from AI processing:', extractedData);

    // Map the extracted data to our form data structure
    const mappedData: Partial<JobFormData> = {
      title: extractedData.title || '',
      description: extractedData.description || '',
      skills: Array.isArray(extractedData.skills) 
        ? extractedData.skills.join(', ') 
        : extractedData.skills || '',
      employmentType: extractedData.employment_type || '',
      timeCommitment: extractedData.time_commitment || '',
      contractDuration: extractedData.contract_duration || '',
      seniorityLevel: extractedData.seniority_level || '',
      locationType: extractedData.location_type || '',
      workplaceBreakdown: extractedData.workplace_breakdown || '',
      hardSkills: Array.isArray(extractedData.hard_skills) 
        ? extractedData.hard_skills.join(', ') 
        : extractedData.hard_skills || '',
      softSkills: Array.isArray(extractedData.soft_skills) 
        ? extractedData.soft_skills.join(', ') 
        : extractedData.soft_skills || '',
      educationLevel: extractedData.education_level || '',
      yearsOfExperience: extractedData.years_of_experience || '',
      languageRequirements: extractedData.language_requirements || '',
      cultureTags: Array.isArray(extractedData.culture_tags) 
        ? extractedData.culture_tags.join(', ') 
        : extractedData.culture_tags || '',
      aiSummary: extractedData.summary || extractedData.ai_summary || '',
    };
    
    // Add company info if available
    if (initialCompany) {
      mappedData.companyId = initialCompany.id;
      mappedData.companyName = initialCompany.name;
    }
    
    console.log('Mapped data for form:', mappedData);
    setFormData(mappedData);
    setCurrentStep('form');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsProcessing(true);
    setProcessingStage("Processing...");

    try {
      const result = await handleAddJob(formData);
      if (result) {
        console.log("Job created successfully with ID:", result);
        resetForm();
      } else {
        console.error("Job creation failed - no ID returned");
      }
    } catch (error) {
      console.error("Error in job creation:", error);
    } finally {
      setIsProcessing(false);
      setProcessingStage("Job created successfully!");
    }
  };

  // Render the appropriate component based on the current step
  if (!isOpen) return null;

  return (
    <>
      <ModalForm
        title={t("jobs.addJobModal.title")}
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleSubmit}
        submitText={isProcessing ? t("common.loading") : t("jobs.addJobModal.saveButton")}
        submitDisabled={isProcessing}
        cancelText={t("jobs.addJobModal.cancelButton")}
      >
        <ProcessingOverlay 
          isProcessing={isProcessing}
          progress={progress}
          status={processingStage}
          message="Creating job offer..."
        />
        <JobCreationMethodModal 
          isOpen={isOpen && currentStep === 'method'}
          onClose={handleClose}
          onSelectMethod={handleMethodSelect}
        />
        
        <JobDescriptionUploadModal 
          isOpen={isOpen && currentStep === 'upload'}
          onClose={() => setCurrentStep('method')}
          onProcessed={handleProcessedData}
        />
        
        <JobDescriptionPasteModal 
          isOpen={isOpen && currentStep === 'paste'}
          onClose={() => setCurrentStep('method')}
          onProcessed={handleProcessedData}
        />
        
        <JobOffersAddWizard 
          isOpen={isOpen && currentStep === 'form'}
          onClose={handleClose}
          onAddJob={handleAddJob}
          initialJobData={formData}
        />
      </ModalForm>
    </>
  );
};
